import * as React from 'react';
import { __ } from 'react-i18n/lib';
import styled from 'styled-components';
import { rem } from 'polished';
import SearchResultsViewer from './SearchResultViewer';
import { SearchFormInput } from './SearchFormInput';
import { Button } from '../General/Button';
import { OutsideClick } from '../../Defaults/OutsideClick';
import Dimmer from '../../Defaults/Dimmer';

// Interfaces
interface SearchFormProps {
  searchResults: {
    productsResult: any;
    categoriesResult: any;
  };
  searchTerm: string;
  redirectToSearchResultPage: (e, type?: string) => void;
  handleSearchTermChange: (e) => void;
  handleFocus: () => void;
  resultsDropdownVisible: boolean;
  hideResultViewer: (p: any, idx: number) => void;
  isMobile?: boolean;
  partialSearchProductsIsFetching: boolean;
  partialSearchCategoriesIsFetching: boolean;
  user: any;
  partialSearchCategoryProducts: any;
}

/*****************/

const SearchFormWrapper = styled.form`
  width: 100%;
`;

const SearchFormContainer = styled.div`
  display: flex;
  position: relative;

  ${({ theme }) => theme.media(0, 'sm')} {
    margin: 0 ${rem(8)} ${rem(4)};
  }
`;

const SearchFormInputWrapper = styled.div`
  @media only screen and (max-width: 1480px) {
    position: relative;
    flex: none;
  }

  flex: 1 0 auto;
  position: relative;

  ${({ theme }) => theme.media(0, 'sm')} {
    margin-bottom: ${rem(8)};
    width: 100%;
  }
`;

const SearchFormSubmit = styled(Button)`
  transition: filter 0.2s ease;

  &:hover {
    filter: brightness(90%);
  }

  ${({ theme }) => theme.media(0, 'sm')} {
    display: none;
  }
`;

export const SearchFormIcon: any = styled.img`
  fill: #cccccc;
  position: absolute;
  top: 50%;
  left: ${rem(15)};
  transform: translate3d(0, -50%, 0);
  width: ${rem(24)};
  height: ${rem(24)};
`;

function SearchForm({
  searchResults,
  redirectToSearchResultPage,
  searchTerm,
  handleFocus,
  handleSearchTermChange,
  resultsDropdownVisible,
  hideResultViewer,
  isMobile = false,
  partialSearchProductsIsFetching,
  partialSearchCategoriesIsFetching,
  user,

  partialSearchCategoryProducts,
}: SearchFormProps) {
  const [isDimmerVisible, setIsDimmerVisible] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const handleKeydown = (e: any) => {
    if (e.key === 'Enter') {
      setIsDimmerVisible(false);
      document.body.style.overflow = 'visible';
      redirectToSearchResultPage(e);

      if (inputRef.current) {
        inputRef.current.blur();
      }
    }
  };

  const onFocus = () => {
    if (isMobile) {
      setIsDimmerVisible(true);
      document.body.style.overflow = 'hidden';
    }
    handleFocus();
  };

  const onBlur = () => {
    document.body.style.overflow = 'visible';
    if (isMobile) {
      setIsDimmerVisible(false);
    }
  };

  return (
    <>
      <SearchFormWrapper autoComplete="off">
        <OutsideClick handleFunction={hideResultViewer}>
          <SearchFormContainer>
            <SearchFormInputWrapper>
              <SearchFormInput
                ref={inputRef}
                isMobile={isMobile}
                name="q"
                type="text"
                placeholder={__('Vyhľadávanie...')}
                value={searchTerm}
                onFocus={onFocus}
                onBlur={onBlur}
                onChange={handleSearchTermChange}
                onKeyDown={handleKeydown}
                autoFocus={isMobile}
              />
              <SearchResultsViewer
                partialSearchProductsIsFetching={
                  partialSearchProductsIsFetching
                }
                partialSearchCategoriesIsFetching={
                  partialSearchCategoriesIsFetching
                }
                visible={resultsDropdownVisible}
                searchResults={searchResults}
                searchTerm={searchTerm}
                redirectToResultPage={redirectToSearchResultPage}
                hideResultViewer={hideResultViewer}
                user={user}
                partialSearchCategoryProducts={partialSearchCategoryProducts}
              />
            </SearchFormInputWrapper>

            <SearchFormSubmit
              type="submit"
              onClick={redirectToSearchResultPage}
            >
              {__('Hľadať')}
            </SearchFormSubmit>

            <SearchFormIcon
              onClick={redirectToSearchResultPage}
              src={'/assets/zemplin/search.svg'}
            />
          </SearchFormContainer>
        </OutsideClick>
      </SearchFormWrapper>
      {isDimmerVisible && isMobile && <Dimmer height={100} top={128} />}
    </>
  );
}

export { SearchForm };
